<script setup lang="ts">
import type { ButtonProps } from '/#/components/base/button/button'
import type { DrawerEmit, DrawerProps } from '/#/components/base/drawer'
import type { ComputedRef } from 'vue'

import merge from 'lodash-es/merge'

const props = withDefaults(defineProps<DrawerProps>(), {
  title: undefined,
  size: 'sm',
  to: '#app-context',
  headerDescription: undefined,
  headerIcon: undefined,
  showFooter: false,
  cancelButton: false,
  confirmButton: false,
  removeBodyPadding: false,
  drawerCloseLength: 1,
})

const emit = defineEmits<DrawerEmit>()

const { t } = useI18n()
const { smallerThan, biggerThan } = useScreenSize()

const isClosing = ref(false)
const IsInputActive = useVModel(props, 'modelValue', emit)
const isVisible = computed(() => props.open || isClosing.value)

const confirmButtonProps: ComputedRef<ButtonProps> = computed(
  () =>
    merge(
      {
        variant: 'solid',
        color: 'tertiary',
        text: t('action.confirm'),
      },
      props.confirmButton,
    ) as ButtonProps,
)

const cancelButtonProps: ComputedRef<ButtonProps> = computed(
  () =>
    merge(
      {
        variant: 'outlined',
        color: 'white',
        text: t('action.cancel'),
      },
      props.cancelButton,
    ) as ButtonProps,
)

/**
 * @name isMultiPages
 * @description determinate closing animation.
 */
const isMultiPages: ComputedRef<boolean> = computed(() => props.drawerCloseLength > 1)

function changeActivity() {
  IsInputActive.value = !IsInputActive.value
}

/**
 * @method close
 * @description Handles closing the drawer with animation
 * @param index {number | 'all'} - Index of the drawer to close, or 'all' to close all drawers
 *
 * This function manages the closing animation and emits the close event.
 * For single page drawers or when closing all pages (index='all'):
 * - Sets isClosing flag to true to trigger animation
 * - Waits 500ms for animation to complete (matches CSS transition)
 * - Emits close event with index
 * - Resets isClosing flag
 *
 * For multi-page drawers when closing individual pages:
 * - Immediately emits close event without animation
 *
 * The animation duration (500ms) should match the CSS transition duration
 * defined in the drawer styles.
 */
function close(index: number | 'all') {
  // Check if this is a single page drawer or if we're closing all pages
  const condition = !isMultiPages.value || index === 'all'

  // Only trigger closing animation for single page or close all
  if (condition) isClosing.value = true

  // Delay emitting close event to allow animation to complete
  setTimeout(
    () => {
      // Emit the close event with the drawer index
      emit('close', index)
      // Reset the closing state after animation
      isClosing.value = false
    },
    // Use 500ms delay for animation, or 0 for immediate close
    condition ? 500 : 0,
  )
}

/** _
 * @name handleEscKey
 * @description When press ESC, close drawer.
 */
function handleEscKey(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    close('all')
  }
}

watch(
  () => props.open,
  (value) => {
    if (value) {
      document.documentElement.classList.add('no-scroll')
      document.addEventListener('keydown', handleEscKey)
    } else {
      document.documentElement.classList.remove('no-scroll')
      document.removeEventListener('keydown', handleEscKey)
    }
  },
)

// When drawer close, remove event listener for keydown
onUnmounted(() => {
  document.removeEventListener('keydown', handleEscKey)
})
</script>

<template>
  <Teleport v-if="isVisible" :to="to">
    <div
      :class="[open && 'is-active', size && `${size}`]"
      class="drawer drawer-background"
     
      @keydown.self.esc="close('all')"
      @click.self.stop="close('all')"
    >
      <div
        class="drawer-wrapper"
        :class="[
          isClosing ? 'closing' : props.open ? 'is-active' : '',
          size && `${size}`,
        ]"
      >
        <div v-if="biggerThan(size)" class="close-button-wrapper">
          <div v-for="index in drawerCloseLength" :key="index" class="close-button">
            <Button
              :icon="{ name: 'delete-cross', color: 'black', size: 'md' }"
              color="white"
              type="button"
              no-padding
              class="drawer-close"
              aria-label="close"
              tabindex="0"
             
              @click="close(index)"
            />
          </div>
        </div>
        <div class="drawer-card">
          <header
            class="drawer-card-head"
            :class="{
              'with-description': headerDescription,
            }"
          >
            <slot name="header">
              <Header
                no-padding
                :title="title"
                :description="headerDescription"
                :icon="headerIcon"
                show-actions
                class="w-full"
              >
                <template v-if="smallerThan(size)" #action>
                  <Button
                    :icon="{ name: 'delete-cross', color: 'black', size: 'md' }"
                    color="white"
                    no-padding
                    class="drawer-close"
                    aria-label="close"
                    tabindex="0"
                   
                    @click="close(-1)"
                  />
                </template>
              </Header>
            </slot>
          </header>
          <div
            class="drawer-card-body"
            :class="{
              'is-bottom-rounded': !showFooter,
              'is-removed-padding': removeBodyPadding,
              'pb-4': showFooter,
            }"
          >
            <div class="inner-content">
              <slot name="content" />
            </div>
          </div>

          <div v-if="showFooter" class="drawer-footer-wrapper">
            <slot name="footer-information" />

            <Divider />

            <div class="drawer-footer">
              <div
                class="footer-wrapper"
                :class="[
                  buttonsPosition === 'center' && 'is-centered',
                  buttonsPosition === 'end' && 'is-end',
                ]"
              >
                <div
                  v-if="footerAction"
                  class="footer-actions"
                  :class="[footerActionProps?.fullWidth && 'w-full']"
                >
                  <Button
                    v-if="footerAction === 'back'"
                    variant="link"
                    color="tertiary"
                    :text="t('action.back')"
                    v-bind="footerActionProps"
                    @click="emit('back')"
                  />
                  <div v-if="footerAction === 'switch'" class="action-with-text">
                    <Switch
                      v-model="IsInputActive"
                     
                      @click="changeActivity()"
                    />
                    <p>
                      {{ t('drawer.remember-me') }}
                    </p>
                  </div>
                  <div
                    v-if="footerAction === 'checkbox'"
                    class="action-with-text"
                    :class="[footerAction === 'checkbox' && 'checkbox']"
                  >
                    <Checkbox v-model="IsInputActive" />
                    <p>
                      {{ t('drawer.dont-show-me-again') }}
                    </p>
                  </div>
                </div>
                <div v-if="cancelButton || confirmButton" class="button-area">
                  <Button
                    v-if="cancelButton"
                    v-bind="cancelButtonProps"
                   
                    @click="close"
                  />
                  <Button
                    v-if="confirmButton"
                    v-bind="confirmButtonProps"
                   
                    @click="emit('confirm')"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
